import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import type { Menu, Order, User, CartItem } from '../types';
import { api } from '../api/client';
import { graphUserSearchRequest } from '../api/msalConfig';
import { formatDateLong, fromISO } from '../utils/dates';
import { formatAllergens, validatePickupCode } from '../utils/allergens';
import { useSettings } from '../contexts/SettingsContext';

interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  companyName: string;
  givenName: string;
  surname: string;
}

interface ChooserModalProps {
  menu: Menu;
  currentUser: User;
  users: User[];
  existingOrders: Order[];
  cartItems: CartItem[];
  editingOrder?: Order;
  initialDish?: 'dish1' | 'dish2' | null;
  onConfirm: (item: Omit<CartItem, 'tempId'>) => void;
  onClose: () => void;
}

type RecipientType = 'self' | 'colleague' | 'guest';

export function ChooserModal({
  menu,
  currentUser,
  users: _users,
  existingOrders,
  cartItems,
  editingOrder,
  initialDish: initialDishProp,
  onConfirm,
  onClose,
}: ChooserModalProps) {
  const initialDish = editingOrder
    ? (editingOrder.dish.name === menu.dish1.name ? 'dish1' : 'dish2')
    : (initialDishProp ?? null);
  const initialRecipient = editingOrder?.recipientType || 'self';
  const initialColleague = editingOrder?.colleagueId || '';
  const initialGuest = editingOrder?.recipientName || '';

  const [selectedDish, setSelectedDish] = useState<'dish1' | 'dish2' | null>(initialDish);
  const [recipientType, setRecipientType] = useState<RecipientType>(initialRecipient);
  const { instance, accounts } = useMsal();
  const [selectedColleague, setSelectedColleague] = useState<string>(initialColleague);
  const [selectedColleagueName, setSelectedColleagueName] = useState<string>('');
  const [colleagueSearch, setColleagueSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [graphResults, setGraphResults] = useState<GraphUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [guestName, setGuestName] = useState<string>(initialGuest);
  const [newPickupCode, setNewPickupCode] = useState('');
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [codeSaving, setCodeSaving] = useState(false);
  const { pickupEnabled } = useSettings();

  const searchGraph = useCallback(async (query: string) => {
    if (query.length < 2) { setGraphResults([]); return; }
    setSearching(true);
    try {
      const account = accounts[0];
      const tokenResponse = await instance.acquireTokenSilent({
        ...graphUserSearchRequest,
        account,
      });
      const company = encodeURIComponent(currentUser.company);
      const q = encodeURIComponent(query);
      const res = await fetch(
        `https://graph.microsoft.com/v1.0/users?$search="displayName:${q}"&$filter=companyName eq '${company}'&$select=id,displayName,mail,companyName,givenName,surname&$top=10&ConsistencyLevel=eventual`,
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}`, ConsistencyLevel: 'eventual' } }
      );
      if (!res.ok) { setGraphResults([]); return; }
      const data = await res.json() as { value: GraphUser[] };
      setGraphResults((data.value || []).filter(u => u.id !== accounts[0]?.localAccountId));
    } catch {
      setGraphResults([]);
    } finally {
      setSearching(false);
    }
  }, [instance, accounts, currentUser.company]);
  const hasPickupCode = !!currentUser.pickupCode;

  const hasSelfOrderForDay = () => {
    const inCart = cartItems.some(
      (c) => c.dateISO === menu.dateISO && c.recipientType === 'self' && (!editingOrder || c.tempId !== editingOrder.id)
    );
    const inOrders = existingOrders.some(
      (o) => o.dateISO === menu.dateISO && o.userId === currentUser.id && o.recipientType === 'self' && !o.cancelled && (!editingOrder || o.id !== editingOrder.id)
    );
    return inCart || inOrders;
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!selectedDish) {
      errs.dish = 'Bitte wähle ein Gericht aus.';
    }

    if (recipientType === 'self') {
      if (pickupEnabled && !hasPickupCode && !showCodeForm) {
        errs.code = 'Du benötigst einen Abholcode.';
      }
      if (showCodeForm && !newPickupCode) {
        errs.newCode = 'Bitte gib einen Code ein.';
      }
      if (showCodeForm && newPickupCode && !validatePickupCode(newPickupCode)) {
        errs.newCode = 'Format: Ziffer-Buchstabe-Ziffer-Buchstabe (z.B. 6F3B)';
      }
      if (hasSelfOrderForDay()) {
        errs.self = 'Du hast für diesen Tag bereits eine eigene Bestellung.';
      }
    }

    if (recipientType === 'colleague' && !selectedColleague) {
      errs.colleague = 'Bitte wähle eine Kollegin/einen Kollegen aus.';
    }

    if (recipientType === 'guest') {
      if (!guestName.trim()) {
        errs.guestName = 'Bitte gib den Namen des Gastes ein.';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleConfirm = async () => {
    if (!validate()) return;

    const dish = selectedDish === 'dish1' ? menu.dish1 : menu.dish2!;

    if (recipientType === 'self' && showCodeForm && newPickupCode) {
      setCodeSaving(true);
      try {
        await api.users.updatePickupCode(newPickupCode.toUpperCase());
        currentUser.pickupCode = newPickupCode.toUpperCase();
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Fehler beim Speichern des Codes';
        setErrors((prev) => ({ ...prev, newCode: errorMessage }));
        setCodeSaving(false);
        return;
      }
      setCodeSaving(false);
    }

    onConfirm({
      dateISO: menu.dateISO,
      dish,
      recipientType,
      recipientName: recipientType === 'guest' ? guestName.trim() : undefined,
      colleagueId: recipientType === 'colleague' ? selectedColleague : undefined,
    });
  };

  const date = fromISO(menu.dateISO);
  const dateLabel = formatDateLong(date);


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{editingOrder ? 'Bestellung ändern' : 'Gericht wählen'}</div>
            <div className="modal-subtitle">{dateLabel}</div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Schließen" />
        </div>

        <div className="modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="form-label">Gericht</div>
            <div
              className={`dish-option ${selectedDish === 'dish1' ? 'selected' : ''}`}
              onClick={() => setSelectedDish('dish1')}
            >
              <div className="dish-option-header">
                <span className="dish-option-name">{menu.dish1.name}</span>
                <span className="dish-option-radio" />
              </div>
              {menu.dish1.allergens.length > 0 && (
                <div className="dish-option-allergens">
                  Allergene: {formatAllergens(menu.dish1.allergens)}
                </div>
              )}
            </div>

            <div
              className={`dish-option ${selectedDish === 'dish2' ? 'selected' : ''}`}
              onClick={() => setSelectedDish('dish2')}
            >
              <div className="dish-option-header">
                <span className="dish-option-name">{menu.dish2.name}</span>
                <span className="dish-option-radio" />
              </div>
              {menu.dish2.allergens.length > 0 && (
                <div className="dish-option-allergens">
                  Allergene: {formatAllergens(menu.dish2.allergens)}
                </div>
              )}
            </div>

            {errors.dish && <div className="form-input-error">{errors.dish}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="form-label">Für wen?</div>
            <div className="segmented">
              {(['self', 'colleague', 'guest'] as RecipientType[]).map((type) => (
                <button
                  key={type}
                  className={`segmented-btn ${recipientType === type ? 'active' : ''}`}
                  onClick={() => {
                    setRecipientType(type);
                    setErrors({});
                  }}
                >
                  {type === 'self' ? 'Für mich' : type === 'colleague' ? 'Kolleg:in' : 'Gast'}
                </button>
              ))}
            </div>
          </div>

          {recipientType === 'self' && (
            <>
              {errors.self && (
                <div className="info-banner error">
                  <span className="info-banner-icon">⚠</span>
                  <span>{errors.self}</span>
                </div>
              )}
              {pickupEnabled && !hasPickupCode && !showCodeForm && !errors.self && (
                <div className="info-banner info">
                  <span className="info-banner-icon">ℹ</span>
                  <div>
                    <div>Du hast noch keinen persönlichen Abholcode.</div>
                    <button
                      className="btn btn-sm"
                      style={{ marginTop: 8, background: 'var(--accent)', color: 'white' }}
                      onClick={() => setShowCodeForm(true)}
                    >
                      Jetzt Code erstellen
                    </button>
                  </div>
                </div>
              )}
              {pickupEnabled && hasPickupCode && (
                <div className="info-banner success">
                  <span className="info-banner-icon">✓</span>
                  <span>Dein Abholcode: <strong style={{ fontFamily: 'monospace' }}>{currentUser.pickupCode}</strong></span>
                </div>
              )}
              {pickupEnabled && showCodeForm && !hasPickupCode && (
                <div className="form-group">
                  <label className="form-label">Neuen Abholcode festlegen</label>
                  <input
                    className={`form-input ${errors.newCode ? 'error' : ''}`}
                    placeholder="z.B. 6F3B"
                    value={newPickupCode}
                    maxLength={4}
                    style={{ textTransform: 'uppercase', letterSpacing: '0.2em', fontFamily: 'monospace', fontSize: 18, textAlign: 'center' }}
                    onChange={(e) => {
                      setNewPickupCode(e.target.value.toUpperCase());
                      setErrors((prev) => ({ ...prev, newCode: '' }));
                    }}
                  />
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500 }}>
                    Format: Ziffer-Buchstabe-Ziffer-Buchstabe (z.B. 6F3B)
                  </div>
                  {errors.newCode && <div className="form-input-error">{errors.newCode}</div>}
                </div>
              )}
            </>
          )}

          {recipientType === 'colleague' && (
            <div className="form-group">
              <label className="form-label">Kollegin / Kollege ({currentUser.company})</label>
              {selectedColleague ? (
                <div className="people-picker-selected">
                  <span>{selectedColleagueName}</span>
                  <button
                    className="people-picker-clear"
                    onClick={() => { setSelectedColleague(''); setSelectedColleagueName(''); setColleagueSearch(''); setGraphResults([]); }}
                  >×</button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input
                    className={`form-input ${errors.colleague ? 'error' : ''}`}
                    placeholder="Name eingeben (min. 2 Zeichen)…"
                    value={colleagueSearch}
                    autoFocus
                    onChange={(e) => {
                      const val = e.target.value;
                      setColleagueSearch(val);
                      setShowSuggestions(true);
                      setErrors((prev) => ({ ...prev, colleague: '' }));
                      searchGraph(val);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  />
                  {showSuggestions && colleagueSearch.length >= 2 && (
                    <div className="people-picker-dropdown">
                      {searching ? (
                        <div className="people-picker-empty">Suche…</div>
                      ) : graphResults.length === 0 ? (
                        <div className="people-picker-empty">Keine Treffer in {currentUser.company}</div>
                      ) : (
                        graphResults.map((u) => (
                          <button
                            key={u.id}
                            className="people-picker-item"
                            onMouseDown={async () => {
                              // Sicherstellen dass Kollege in Foody-DB existiert
                              const nameParts = u.displayName.split(' ');
                              await api.users.ensure({
                                oid: u.id,
                                firstName: u.givenName || nameParts[0] || '',
                                lastName: u.surname || nameParts.slice(1).join(' ') || '',
                                email: u.mail || '',
                                company: u.companyName || currentUser.company,
                              });
                              setSelectedColleague(u.id);
                              setSelectedColleagueName(u.displayName);
                              setColleagueSearch('');
                              setShowSuggestions(false);
                              setErrors((prev) => ({ ...prev, colleague: '' }));
                            }}
                          >
                            <span className="people-picker-name">{u.displayName}</span>
                            <span className="people-picker-email">{u.mail}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {errors.colleague && <div className="form-input-error">{errors.colleague}</div>}
            </div>
          )}

          {recipientType === 'guest' && (
            <>
              <div className="form-group">
                <label className="form-label">Name des Gastes</label>
                <input
                  className={`form-input ${errors.guestName ? 'error' : ''}`}
                  placeholder="Vor- und Nachname"
                  value={guestName}
                  onChange={(e) => {
                    setGuestName(e.target.value);
                    setErrors((prev) => ({ ...prev, guestName: '' }));
                  }}
                />
                {errors.guestName && <div className="form-input-error">{errors.guestName}</div>}
              </div>
              {pickupEnabled && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500 }}>
                  Der Abholcode für den Gast wird automatisch erzeugt und nach dem Bestellen angezeigt.
                </div>
              )}
            </>
          )}

          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
            <button
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={codeSaving}
            >
              {codeSaving ? 'Speichern…' : editingOrder ? 'Änderung übernehmen' : 'In den Warenkorb'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
